// ─────────────────────────────────────────────────────────────
// ProductService — products 테이블 책임 격리
// products는 카탈로그의 표준 제품명 마스터 테이블
// (category/form_factor는 v7.0부터 NULL 유지 — 카탈로그 단순화)
// ─────────────────────────────────────────────────────────────
import { supabase } from '../supabase';

export const ProductService = {
  // 카탈로그 페이지네이션 조회
  // returns: { data, count }
  async list({ search, category, formFactor, page = 0, pageSize = 50 } = {}) {
    // v7.281: 시리얼 길이 컬럼 추가 (제품 표준화 관리에서 인라인 편집)
    //   ⚠ 컬럼이 없으면(v7.277 SQL 미실행) select 자체가 실패해 카탈로그가 안 뜬다.
    //     그래서 실패 시 기본 컬럼으로 재시도한다 — 마이그레이션 전에도 화면은 열려야 한다.
    let q = supabase.from('products').select('id,product_name,category,form_factor,created_at,serial_min_len,serial_max_len', { count: 'exact' });
    if (search && search.trim()) q = q.ilike('product_name', `%${search.trim()}%`);
    if (category) {
      if (category === '__UNCAT__') q = q.is('category', null); // v7.45: 미분류만 보기
      else q = q.eq('category', category);
    }
    if (formFactor) {
      if (formFactor === '__NONE__') q = q.is('form_factor', null);
      else q = q.eq('form_factor', formFactor);
    }
    const { data, count, error } = await q.order('product_name').range(page * pageSize, (page + 1) * pageSize - 1);
    if (error) {
      // v7.281: serial_* 컬럼 없음 → 기본 컬럼만으로 재시도
      let q2 = supabase.from('products').select('id,product_name,category,form_factor,created_at', { count: 'exact' });
      if (search && search.trim()) q2 = q2.ilike('product_name', `%${search.trim()}%`);
      if (category) {
        if (category === '__UNCAT__') q2 = q2.is('category', null);
        else q2 = q2.eq('category', category);
      }
      if (formFactor) {
        if (formFactor === '__NONE__') q2 = q2.is('form_factor', null);
        else q2 = q2.eq('form_factor', formFactor);
      }
      const r2 = await q2.order('product_name').range(page * pageSize, (page + 1) * pageSize - 1);
      return { data: r2.data || [], count: r2.count || 0, noSerialCols: true };
    }
    return { data: data || [], count: count || 0 };
  },

  // 부분 검색 (%query%) — 검색 모달, 입고 등록 등에서 사용
  async searchByName(query, limit = 10) {
    if (!query) return [];
    const { data } = await supabase.from('products')
      .select('product_name,category')
      .ilike('product_name', `%${query}%`)
      .order('product_name')
      .limit(limit);
    return data || [];
  },

  // 이름 배열 → product 정보 일괄 조회 (입고/출고 시 카테고리 매핑)
  async findByNames(names) {
    if (!names || names.length === 0) return [];
    const { data } = await supabase.from('products')
      .select('product_name,category')
      .in('product_name', names);
    return data || [];
  },

  // 이름 배열 중 이미 존재하는 것만 추출 (중복 INSERT 방지용)
  async existingNames(names) {
    if (!names || names.length === 0) return new Set();
    const { data } = await supabase.from('products')
      .select('product_name')
      .in('product_name', names);
    return new Set((data || []).map(p => p.product_name));
  },

  // 정확 매칭 (eq) — 입고 6단계 매칭의 1단계
  async findExact(name) {
    if (!name) return null;
    const { data } = await supabase.from('products')
      .select('product_name,category')
      .eq('product_name', name)
      .limit(1);
    return (data && data.length > 0) ? data[0] : null;
  },

  // 대소문자 무시 정확 매칭 (입고 6단계 매칭의 4단계)
  async findCaseInsensitive(name, limit = 2) {
    if (!name) return [];
    const { data } = await supabase.from('products')
      .select('product_name,category')
      .ilike('product_name', name)
      .limit(limit);
    return data || [];
  },

  // 부분 매칭 (%query%) — 입고 6단계 매칭의 5단계, 결과 1개면 자동 선택
  async findPartial(query, limit = 2) {
    if (!query) return [];
    const { data } = await supabase.from('products')
      .select('product_name,category')
      .ilike('product_name', `%${query}%`)
      .limit(limit);
    return data || [];
  },

  // 신규 제품 등록 (단일)
  async create({ product_name, category = null, form_factor = null }) {
    const { error } = await supabase.from('products').insert({ product_name, category, form_factor });
    if (error) return { ok: false, msg: error.message };
    return { ok: true };
  },

  // upsert (충돌 시 업데이트)
  async upsert({ product_name, category = null, form_factor = null }) {
    const { error } = await supabase.from('products').upsert(
      { product_name, category, form_factor },
      { onConflict: 'product_name' }
    );
    if (error) return { ok: false, msg: error.message };
    return { ok: true };
  },

  // 카테고리/폼팩터만 업데이트 (제품명은 그대로) — id 기준
  async updateMeta(id, { category = null, form_factor = null }) {
    const { error } = await supabase.from('products')
      .update({ category, form_factor })
      .eq('id', id);
    if (error) return { ok: false, msg: error.message };
    return { ok: true };
  },

  // v7.281: 시리얼 길이 저장 (제품 표준화 관리 인라인 편집)
  //   len 이 null 이면 지정 해제 → 스캔 시 길이 검사를 건너뛴다 (카톤 학습으로 폴백).
  //   ⚠ 접두어(serial_prefix)는 여기서 건드리지 않는다. 접두어는 생산 로트를 따라가므로
  //     제품에 박아두면 다음 로트가 전부 거부된다 (v7.277 근거 참조).
  async updateSerialLen(id, len) {
    const v = (len === null || len === undefined || len === '') ? null : parseInt(len, 10);
    if (v !== null && (!Number.isFinite(v) || v < 1 || v > 40)) {
      return { ok: false, msg: '길이는 1~40 사이여야 합니다' };
    }
    const { error } = await supabase.from('products')
      .update({ serial_min_len: v, serial_max_len: v })
      .eq('id', id);
    if (error) return { ok: false, msg: error.message };
    return { ok: true };
  },

  // 제품 삭제 — id 기준
  async remove(id) {
    const { error } = await supabase.from('products').delete().eq('id', id);
    if (error) return { ok: false, msg: error.message };
    return { ok: true };
  },

  // 제품명 기준 삭제 (rename 워크플로우에서 옛 이름 정리용)
  async removeByName(name) {
    const { error } = await supabase.from('products').delete().eq('product_name', name);
    if (error) return { ok: false, msg: error.message };
    return { ok: true };
  },

  // 다수 INSERT (bulk)
  async bulkInsert(items) {
    if (!items || items.length === 0) return { ok: true };
    const { error } = await supabase.from('products').insert(items);
    if (error) return { ok: false, msg: error.message };
    return { ok: true };
  },
};
